import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { useAccount, useMsal } from "@azure/msal-react";
import { notifications } from "@mantine/notifications";
import { CHAT_COMPLETIONS_URL } from "../hooks/useChatCompletion";
import { loginRequest } from "../utils/authConfig";

const GRAPH_API_BASE_URL = "https://graph.microsoft.com/v1.0";

interface TranslateExchangeIdsResult {
    sourceId: string;
    targetId?: string;
    errorDetails?: {
        message: string;
        code: string;
    };
}

interface TranslateExchangeIdsResponse {
    value: TranslateExchangeIdsResult[];
}

export const useArtifactHandler = () => {
    const { instance } = useMsal();
    const account = useAccount();
    const apiKey = account?.idToken;

    type ResolvedOpenTarget = { kind: "url"; url: string } | { kind: "blob"; url: string };

    const resolveStorageOpenTarget = async (
        storageApiUrl: string,
    ): Promise<ResolvedOpenTarget | null> => {
        try {
            const token = `Bearer ${apiKey}`;

            const res = await fetch(storageApiUrl, {
                method: "GET",
                headers: {
                    Authorization: token,
                    Accept: "*/*",
                },
                redirect: "follow",
            });
            // 200 Open as blob
            if (res.ok) {
                const blob = await res.blob();
                const blobUrl = URL.createObjectURL(blob);
                return { kind: "blob", url: blobUrl };
            }

            // Auth fallback
            const isInIframe = window.self !== window.top;
            if (isInIframe) {
                await instance.loginPopup(loginRequest());
            } else {
                await instance.loginRedirect(loginRequest());
            }

            notifications.show({
                title: "Error opening file",
                message: `Could not open file (status ${res.status})`,
                color: "red",
            });
            return null;
        } catch (error) {
            console.error(`Error resolving storage file: ${storageApiUrl}`, error);
            notifications.show({
                title: "Error opening file",
                message: "Unexpected error while opening file.",
                color: "red",
            });
            return null;
        }
    };

    // Check if a URL is an Outlook email URL with ImmutableEntryId
    const isOutlookEmailUrl = (url: string): boolean => {
        return url.includes("outlook.office365.com") && url.includes("ItemID=");
    };

    // Extract ImmutableEntryId from Outlook URL
    const extractImmutableEntryId = (url: string): string | null => {
        try {
            const urlObj = new URL(url);
            const itemId = urlObj.searchParams.get("ItemID");
            return itemId;
        } catch {
            return null;
        }
    };

    // Get access token for Microsoft Graph API
    const getGraphAccessToken = async (): Promise<string | null> => {
        if (!account) return null;

        try {
            const response = await instance.acquireTokenSilent({
                scopes: ["Mail.ReadBasic"],
                account: account,
            });
            return response.accessToken;
        } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
                try {
                    const isInIframe = window.self !== window.top;
                    if (isInIframe) {
                        const response = await instance.acquireTokenPopup({
                            scopes: ["Mail.ReadBasic"],
                            account: account,
                        });
                        return response.accessToken;
                    }
                    // acquireTokenRedirect returns void, so we need to handle it differently
                    // The token will be available after the redirect completes
                    await instance.acquireTokenRedirect({
                        scopes: ["Mail.ReadBasic"],
                        account: account,
                    });
                    return null;
                } catch (interactiveError) {
                    console.error("Error acquiring token interactively:", interactiveError);
                    return null;
                }
            }
            console.error("Error acquiring Graph API token:", error);
            return null;
        }
    };

    // Translate ImmutableEntryId to restId using Microsoft Graph API
    const translateExchangeId = async (immutableEntryId: string): Promise<string | null> => {
        const accessToken = await getGraphAccessToken();
        if (!accessToken) {
            console.error("Failed to get access token for Graph API");
            return null;
        }

        try {
            const response = await fetch(`${GRAPH_API_BASE_URL}/me/translateExchangeIds`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    inputIds: [immutableEntryId],
                    sourceIdType: "restImmutableEntryId",
                    targetIdType: "restId",
                }),
            });

            if (!response.ok) {
                console.error(
                    "Failed to translate exchange ID:",
                    response.status,
                    response.statusText,
                );
                return null;
            }

            const data: TranslateExchangeIdsResponse = await response.json();
            if (data.value && data.value.length > 0) {
                const result = data.value[0];

                // Check if the response contains an error (e.g., user doesn't have access to the email)
                if (result.errorDetails) {
                    console.warn(
                        "Email ID translation error:",
                        result.errorDetails.code,
                        result.errorDetails.message,
                    );
                    return null;
                }

                if (result.targetId) {
                    return result.targetId;
                }
            }
            return null;
        } catch (error) {
            console.error("Error translating exchange ID:", error);
            return null;
        }
    };

    // Build Outlook URL with translated restId
    const buildOutlookUrlWithRestId = (restId: string): string => {
        const encodedId = encodeURIComponent(restId);
        return `https://outlook.office365.com.mcas.ms/mail/deeplink/read/${encodedId}`;
    };

    // Open Outlook email URL, translating ImmutableEntryId if necessary
    const openOutlookEmailUrl = async (originalUrl: string): Promise<void> => {
        const immutableEntryId = extractImmutableEntryId(originalUrl);
        if (!immutableEntryId) {
            window.open(originalUrl, "_blank");
            return;
        }

        const restId = await translateExchangeId(immutableEntryId);
        if (restId) {
            const newUrl = buildOutlookUrlWithRestId(restId);
            window.open(newUrl, "_blank");
        } else {
            // Do not open the link if translation fails (e.g., user doesn't have access to the email)
            notifications.show({
                title: "Unable to open email",
                message: "You may not have access to this email, or it may have been deleted.",
                color: "red",
            });
        }
    };

    const getFullUrl = (url: string): string => {
        // If it's already a full URL, just clean up any duplicates
        if (url.startsWith("http") || url.startsWith("file://")) {
            // Clean up duplicate v1/storage segments if they exist
            if (url.includes("/v1/storage/v1/storage/")) {
                return url.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
            }
            return url;
        }
        // Check if it's a storage URL pattern that needs the base URL prepended
        if (url.startsWith("v1/storage/")) {
            const fullUrl = `${CHAT_COMPLETIONS_URL}/${url}`;
            // Clean up any duplicate v1/storage segments
            if (fullUrl.includes("/v1/storage/v1/storage/")) {
                return fullUrl.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
            }
            return fullUrl;
        }
        // Handle other relative URLs
        const hrefWithoutSandbox = url
            .replace("sandbox:", "")
            .replace("http://example.com", "")
            .replace("https://example.com", "");

        const decodedHref = decodeURIComponent(hrefWithoutSandbox);
        // Handle special case for storage file URLs
        const finalHref = decodedHref.includes("object_name=/")
            ? decodedHref.replace("object_name=/", "object_name=")
            : decodedHref;

        // Clean up any duplicate v1/storage segments in the final URL
        const fullUrl = `${CHAT_COMPLETIONS_URL}${finalHref.startsWith("/") ? "" : "/"}${finalHref}`;
        if (fullUrl.includes("/v1/storage/v1/storage/")) {
            return fullUrl.replace(/\/v1\/storage(\/v1\/storage\/)+/g, "/v1/storage/");
        }
        return fullUrl;
    };

    const ARTIFACT_EXTENSIONS = [
        ".pdf",
        ".html",
        ".doc",
        ".docx",
        ".ppt",
        ".pptx",
        ".xls",
        ".xlsx",
        ".msg",
        ".csv",
        ".json",
        ".txt",
    ];

    const isArtifactURL = (url?: string): boolean => {
        if (!url) return false;
        // Check if it's a storage URL pattern (e.g., v1/storage/file?object_name=...)
        if (url.startsWith("v1/storage/") || url.includes("/v1/storage/")) {
            return true;
        }

        // Check if it ends with known artifact extensions
        return ARTIFACT_EXTENSIONS.some((extension) => url.toLowerCase().endsWith(extension));
    };

    const getPdfUrl = async (pdfUrl: string): Promise<string | null> => {
        const target = await resolveStorageOpenTarget(pdfUrl);
        return target?.url ?? null;
    };

    const openStorageFile = async (storageApiUrl: string) => {
        const target = await resolveStorageOpenTarget(storageApiUrl);
        if (!target) return;

        const newWindow = window.open(target.url, "_blank", "");

        if (target.kind === "blob") {
            if (newWindow) {
                newWindow.onload = () => URL.revokeObjectURL(target.url);
            } else {
                const a = document.createElement("a");
                a.href = target.url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                a.click();
                setTimeout(() => URL.revokeObjectURL(target.url), 10_000);
            }
        }
    };

    // Simple function to open URL without event handling
    const openUrl = async (source: string) => {
        if (!source) return;

        const fullUrl = getFullUrl(source);

        // Handle Outlook email URLs with ImmutableEntryId translation
        if (isOutlookEmailUrl(fullUrl)) {
            await openOutlookEmailUrl(fullUrl);
            return;
        }

        if (isArtifactURL(source)) {
            if (fullUrl.startsWith("file://")) {
                // For file:// URLs, copy to clipboard
                navigator.clipboard.writeText(fullUrl).then(() => {
                    notifications.show({
                        title: "URL Copied",
                        message:
                            "This is a local file that cannot be opened directly in your browser. Please open a new browser tab and paste the copied URL.",
                        color: "green",
                        autoClose: 4000,
                    });
                });
                return;
            }

            if (fullUrl.startsWith(CHAT_COMPLETIONS_URL)) {
                await openStorageFile(fullUrl);
            } else {
                window.open(fullUrl, "_blank");
            }
        } else {
            window.open(fullUrl, "_blank");
        }
    };

    // Handle artifact clicks with optional disclaimer callback
    const handleArtifactClick = async (
        e: React.MouseEvent<HTMLAnchorElement>,
        source: string,
        disclaimerMessages: string[],
        onShowDisclaimer?: (messages: string[], link: string) => void,
    ) => {
        // Prevent default immediately to stop the browser from following the href
        // This must be called synchronously at the start before any async operations
        e.preventDefault();

        if (!source) return;

        // If there are disclaimer messages and a callback, show disclaimer
        if (disclaimerMessages.length > 0 && onShowDisclaimer) {
            onShowDisclaimer(disclaimerMessages, source);
            return;
        }

        // Otherwise, open the URL directly
        await openUrl(source);
    };

    return {
        getFullUrl,
        isArtifactURL,
        getPdfUrl,
        openStorageFile,
        openUrl,
        handleArtifactClick,
    };
};
